import { useTranslation } from 'react-i18next';
import { Control, UseFormWatch, UseFormSetValue, Controller, useFormState } from 'react-hook-form';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldGroup, FieldSeparator, FieldSet } from '@/components/ui/field';
import { ContactSelector } from '@/widgets/contact-selector';
import { ConsignmentSummary } from '@/entities/consignments';

import { ConsignmentFormValues } from '../model/validation';
import { SrfUserEntryNote } from './srf-user-entry-note';

interface PointsOfContactCardProps {
  control: Control<ConsignmentFormValues>;
  watch: UseFormWatch<ConsignmentFormValues>;
  setValue: UseFormSetValue<ConsignmentFormValues>;
  consignmentData?: ConsignmentSummary;
  /** Names for prefilled ids the option lists may not hold. */
  prefillLabels?: Partial<Record<keyof ConsignmentFormValues, string>>;
  /** SRF text an import could not resolve; seeds the control's search query. */
  serverFieldHints?: Partial<Record<keyof ConsignmentFormValues, string>>;
  isReadOnly?: boolean;
}

export const PointsOfContactCard = ({
  control,
  watch,
  setValue,
  consignmentData,
  prefillLabels,
  serverFieldHints,
  isReadOnly = false
}: PointsOfContactCardProps) => {
  const { t } = useTranslation();
  const { errors } = useFormState({ control });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{t('newcmr.partner.box')}</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <FieldSet>
            <SrfUserEntryNote section="sender" />
            <Controller
              name="senderContact"
              control={control}
              render={({ field }) => (
                <ContactSelector
                  organizationLabel={t('newcmr.senderorg.label')}
                  contactLabel={t('newcmr.sendercontact.label')}
                  organizationId={watch('senderOrganization')}
                  organizationName={
                    consignmentData?.senderContact?.organization?.name ?? prefillLabels?.senderOrganization
                  }
                  contactName={consignmentData?.senderContact?.contact?.name ?? prefillLabels?.senderContact}
                  contactId={field.value}
                  onOrganizationChange={(id) => {
                    setValue('senderOrganization', id ?? '', { shouldValidate: true });
                    setValue('senderContact', '');
                  }}
                  onContactChange={field.onChange}
                  disabled={isReadOnly}
                  required
                  organizationError={errors.senderOrganization?.message}
                  contactError={errors.senderContact?.message}
                  organizationInitialQuery={serverFieldHints?.senderOrganization}
                  contactInitialQuery={serverFieldHints?.senderContact}
                />
              )}
            />
          </FieldSet>

          <FieldSeparator />

          <FieldSet>
            <SrfUserEntryNote section="receiver" />
            <Controller
              name="receiverContact"
              control={control}
              render={({ field }) => (
                <ContactSelector
                  organizationLabel={t('newcmr.receiverorg.label')}
                  contactLabel={t('newcmr.receivercontact.label')}
                  organizationId={watch('receiverOrganization')}
                  organizationName={
                    consignmentData?.receiverContact?.organization?.name ?? prefillLabels?.receiverOrganization
                  }
                  contactName={consignmentData?.receiverContact?.contact?.name ?? prefillLabels?.receiverContact}
                  contactId={field.value}
                  onOrganizationChange={(id) => {
                    setValue('receiverOrganization', id ?? '', { shouldValidate: true });
                    setValue('receiverContact', '');
                  }}
                  onContactChange={field.onChange}
                  disabled={isReadOnly}
                  required
                  organizationError={errors.receiverOrganization?.message}
                  contactError={errors.receiverContact?.message}
                  organizationInitialQuery={serverFieldHints?.receiverOrganization}
                  contactInitialQuery={serverFieldHints?.receiverContact}
                />
              )}
            />
          </FieldSet>

          <FieldSeparator />

          <FieldSet>
            <SrfUserEntryNote section="clearingAgent" />
            <Controller
              name="clearingAgentContact"
              control={control}
              render={({ field }) => (
                <ContactSelector
                  organizationLabel={t('newcmr.invoiceorg.label')}
                  contactLabel={t('newcmr.invoicecontact.label')}
                  organizationId={watch('clearingAgent')}
                  organizationName={consignmentData?.clearingAgent?.organization?.name ?? prefillLabels?.clearingAgent}
                  contactName={consignmentData?.clearingAgent?.contact?.name ?? prefillLabels?.clearingAgentContact}
                  contactId={field.value}
                  onOrganizationChange={(id) => {
                    setValue('clearingAgent', id);
                    setValue('clearingAgentContact', '');
                  }}
                  onContactChange={field.onChange}
                  disabled={isReadOnly}
                />
              )}
            />
          </FieldSet>
        </FieldGroup>
      </CardContent>
    </Card>
  );
};
